import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, type ConsoleConfig, type Me } from './api';
import { forgetAllMints } from '../previews/mint-store';
import { DEFAULT_PREVIEW_HOST_SUFFIX, DEFAULT_PUBLIC_HOST_SUFFIX } from './hosts';

/**
 * Who is signed in (GET /v1/me) and what this environment offers for
 * signing in (GET /v1/console/config). `me === null` after loading means
 * no session — the router sends you to /login.
 */
interface SessionState {
  loading: boolean;
  me: Me | null;
  config: ConsoleConfig | null;
  refresh: () => Promise<Me | null>;
  signOut: () => Promise<void>;
  /**
   * Let the identity provider register its own sign-out. Signing out of
   * the API alone was not a sign-out: Clerk's session outlived it, so
   * /login re-exchanged the still-valid Clerk token for a fresh API
   * session and put you straight back in the console. SessionProvider
   * sits above ClerkProvider (it fetches the publishable key), so the
   * provider hands its sign-out down rather than being called directly.
   */
  registerIdpSignOut: (fn: (() => Promise<unknown>) | null) => void;
}
/** Set by signOut, read and cleared by the login page. Session-scoped on
 *  purpose: it must not outlive the tab. */
export const SIGNED_OUT_KEY = 'cz-signed-out';

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<ConsoleConfig | null>(null);
  const refresh = useCallback(async (): Promise<Me | null> => {
    try { const m = (await api.get<Me>('/v1/me')).data; setMe(m); return m; }
    catch (e) { if (e instanceof ApiError && (e.status === 401 || e.status === 403)) { setMe(null); return null; } throw e; }
  }, []);
  useEffect(() => {
    (async () => {
      try { setConfig((await api.get<ConsoleConfig>('/v1/console/config')).data); } catch { setConfig({ env: 'unknown', clerk_publishable_key: null, github_login: false, public_host_suffix: DEFAULT_PUBLIC_HOST_SUFFIX, preview_host_suffix: DEFAULT_PREVIEW_HOST_SUFFIX }); }
      // Never let a bootstrap failure strand `loading`: ClerkGate renders
      // nothing while it is true, so a 500 or an offline Worker would
      // white-page every route — /login included, leaving no way back in.
      try { await refresh(); } catch { setMe(null); }
      setLoading(false);
    })();
  }, [refresh]);
  const idpSignOut = useRef<(() => Promise<unknown>) | null>(null);
  const registerIdpSignOut = useCallback((fn: (() => Promise<unknown>) | null) => { idpSignOut.current = fn; }, []);
  const signOut = useCallback(async () => {
    // Order matters, and not the way it first appears. Clerk's signOut()
    // navigates to `afterSignOutUrl` via window.location, which can abort
    // an in-flight fetch — so calling it first could kill the DELETE below
    // and leave the API cookie alive, reproducing the very loop this
    // fixes. The API session goes first; Clerk's redirect comes last.
    //
    // SIGNED_OUT is what makes it deterministic rather than merely
    // likely. Clerk's signOut is a no-op that resolves successfully when
    // clerk-js has not hydrated yet (IsomorphicClerk queues premount
    // calls and returns undefined), so awaiting it proves nothing. The
    // marker tells /login that this was a deliberate sign-out, and
    // AfterClerk refuses to exchange a surviving Clerk token while it is
    // set — whatever Clerk's own state turns out to be.
    try { sessionStorage.setItem(SIGNED_OUT_KEY, '1'); } catch { /* private mode */ }
    // An unread preview URL lives outside React (previews/mint-store) so
    // that no re-render can lose it — which also means nothing clears it
    // on its own. Two people can share a tab, and this sign-out does not
    // reliably end in a page navigation: Clerk's redirect is conditional
    // on an IdP being registered, so on the other paths the tab's memory
    // survives into the next session. Drop them here, explicitly.
    forgetAllMints();
    try { await api.del('/v1/auth/session'); } finally { setMe(null); }
    try { await idpSignOut.current?.(); } catch { /* the marker still holds */ }
  }, []);
  return <Ctx.Provider value={{ loading, me, config, refresh, signOut, registerIdpSignOut }}>{children}</Ctx.Provider>;
}
/**
 * Us, not a customer. The scopes that no membership role carries —
 * `registry:write`, `creds:write`, `tokens:mint` — are exactly the ones
 * `super_admin` gets from its `'*'`, so this is the render-time question
 * behind every staff-only control.
 */
export const isStaff = (me: Me | null): boolean => me?.user?.global_role === 'super_admin';

/**
 * May this person change things for this organisation? Staff anywhere,
 * an admin of that org, and nobody else — an editor's membership is a
 * site, not the org. The same question the API answers with
 * `config:settings:write`, asked here only to decide what to render.
 */
export function isClientAdmin(me: Me | null, client: string): boolean {
  return isStaff(me)
    || (me?.user?.memberships ?? []).some((m) => m.client_id === client && m.role === 'admin');
}

/**
 * May this person change this SITE's content and theme? Everyone
 * `isClientAdmin` admits, plus an editor whose membership names the
 * site. The question the API answers with `config:write`.
 */
export function isSiteEditor(me: Me | null, client: string, site: string): boolean {
  return isClientAdmin(me, client)
    || (me?.user?.memberships ?? []).some((m) => m.client_id === client && m.role === 'editor' && m.site_key === site);
}

/**
 * Is Search (Google) shown to this person? The API's own rule
 * (HEADLESS_GOOGLE_SEARCH): everyone when 'all', staff when 'staff',
 * nobody when Google is not configured. Asked only to decide what to
 * render — the API answers 404 to anyone this says no to.
 */
export function canSeeSearch(me: Me | null, config: ConsoleConfig | null): boolean {
  const mode = config?.google_search ?? null;
  return mode === 'all' || (mode === 'staff' && isStaff(me));
}

export function useSession(): SessionState {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside <SessionProvider>');
  return s;
}
